/**
 * Monthly Energy Summary Component
 * Displays energy consumption/production for a selected month with daily breakdown
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Grid,
  CircularProgress,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import {
  Autorenew as Loader2,
  CalendarToday as Calendar,
  Download,
  Refresh as RefreshCw,
  ErrorOutline as AlertCircle
} from '@mui/icons-material';
import { EnergyChart } from './EnergyChart';
import { reportingApi, EnergySummaryResponse, EnergyTagData } from '../../services/reportingApi';

interface MonthlyEnergySummaryProps {
  initialMonth?: number;
  initialYear?: number;
}

export const MonthlyEnergySummary: React.FC<MonthlyEnergySummaryProps> = ({
  initialMonth = new Date().getMonth() + 1,
  initialYear = new Date().getFullYear()
}) => {
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [energyData, setEnergyData] = useState<EnergySummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Year options (last 3 years + current + next)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const fetchEnergyData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await reportingApi.getEnergySummary(selectedMonth, selectedYear);
      setEnergyData(data);
      
      if (data.energy_data.length === 0) {
        setError('No energy data available for the selected period');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load energy data';
      setError(errorMessage);
      console.error('Error loading energy data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data when month/year changes
  useEffect(() => {
    fetchEnergyData();
  }, [selectedMonth, selectedYear]);

  const handleRefresh = () => {
    fetchEnergyData();
  };

  const handleExportData = () => {
    if (!energyData) return;

    // Create CSV data
    const csvData = [];
    csvData.push(['Date', 'Tag', 'Energy (kWh)', 'Reset Detected', 'Data Points']);
    
    energyData.energy_data.forEach(tagData => {
      tagData.daily_breakdown.forEach(day => {
        csvData.push([
          day.date,
          tagData.tag_name,
          day.energy?.toString() || 'N/A',
          day.reset_detected ? 'Yes' : 'No',
          day.data_points.toString()
        ]);
      });
    });

    // Download CSV
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `energy-summary-${selectedYear}-${selectedMonth.toString().padStart(2, '0')}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const monthName = months.find(m => m.value === selectedMonth)?.label || 'Unknown';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header with controls */}
      <Card>
        <CardHeader>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Calendar color="primary" />
              <Typography variant="h6" component="h2">
                Monthly Energy Summary
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRefresh}
                disabled={loading}
                startIcon={loading ? <Loader2 /> : <RefreshCw />}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportData}
                disabled={!energyData || loading}
                startIcon={<Download />}
              >
                Export CSV
              </Button>
            </Box>
          </Box>
        </CardHeader>
        
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth.toString()}
                label="Month"
                onChange={(e: SelectChangeEvent) => setSelectedMonth(parseInt(e.target.value))}
              >
                {months.map(month => (
                  <MenuItem key={month.value} value={month.value.toString()}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear.toString()}
                label="Year"
                onChange={(e: SelectChangeEvent) => setSelectedYear(parseInt(e.target.value))}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Chip 
              label={`${monthName} ${selectedYear}`} 
              variant="outlined"
              sx={{ ml: 2 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Loading state */}
      {loading && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="center" py={6}>
              <CircularProgress size={32} sx={{ mr: 1 }} />
              <Typography color="text.secondary">Loading energy data...</Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Error state */}
      {error && !loading && (
        <Alert severity="error" icon={<AlertCircle />}>
          {error}
        </Alert>
      )}

      {/* Energy data charts */}
      {energyData && !loading && energyData.energy_data.map((tagData: EnergyTagData) => (
        <EnergyChart
          key={tagData.tag_name}
          data={tagData.daily_breakdown}
          title={`${tagData.tag_description} (${tagData.tag_name})`}
          units={tagData.tag_units}
          chartType="line"
          showResetIndicators={true}
          height={400}
        />
      ))}

      {/* Summary statistics */}
      {energyData && !loading && energyData.energy_data.length > 0 && (
        <Card>
          <CardHeader>
            <Typography variant="h6" component="h3">
              Monthly Summary
            </Typography>
          </CardHeader>
          <CardContent>
            <Grid container spacing={2}>
              {energyData.energy_data.map((tagData: EnergyTagData) => (
                <Grid item xs={12} sm={6} md={3} key={tagData.tag_name}>
                  <Box p={2} border={1} borderColor="divider" borderRadius={1}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium" gutterBottom>
                      {tagData.tag_name}
                    </Typography>
                    <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
                      {tagData.monthly_total.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tagData.tag_units} total
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                      {tagData.days_with_data} days with data
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
